import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import "./ConfirmDialogAction.css";

interface ConfirmActionDialogProps {
  open: boolean;
  action: "add" | "edit" | "delete";
  entryNumber?: number | string;
  meta?: {
    divisionStatus?: string;
    oldDivisionName?: string;
    newDivisionName?: string;
    oldProgramName?: string;
    newProgramName?: string;
    talktapeStatus?: string;
    divisionId?: number;
    oldDivisionStatus?: string;
  };
  onConfirm: () => void;
  onCancel: () => void;
}

const ICONS = {
  add: <AddCircleOutlineIcon />,
  edit: <EditOutlinedIcon />,
  delete: <DeleteOutlineIcon />,
};

const TITLES = {
  add: "Add Entry",
  edit: "Edit Entry",
  delete: "Delete Entry",
};

const WarningBox = ({ color, children }: { color: "yellow" | "red"; children: React.ReactNode }) => (
  <Box className={`warning-box warning-box--${color}`}>
    <WarningAmberIcon className={`warning-icon warning-icon--${color}`} fontSize="small" />
    <Typography variant="body2">{children}</Typography>
  </Box>
);

const ImagePreview = ({ src, alt }: { src: string; alt: string }) => (
  <Box className="image-preview">
    <img
      src={src}
      alt={alt}
      className="image-preview__img"
      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
    />
  </Box>
);
const ConfirmActionDialog = ({
  open, action, entryNumber,
  meta = {},
  onConfirm, onCancel,
}: ConfirmActionDialogProps) => {
  const {
    divisionStatus, oldDivisionName, newDivisionName,
    oldProgramName, newProgramName,
    oldDivisionStatus,
    talktapeStatus, divisionId,
  } = meta;

  const divisionChanged = !!oldDivisionName && oldDivisionName !== newDivisionName;

  const renderAddContent = () => {
    const willReset = divisionStatus && divisionStatus !== "judging";
    if (!willReset) {
      return <Typography variant="body2">Are you sure you want to add entry <strong>#{entryNumber}</strong>?</Typography>;
    }
    return (
      <WarningBox color="yellow">
        Adding entry <strong>#{entryNumber}</strong> to division <strong>{oldDivisionName}</strong> will
        set its status to <strong>Judging</strong>. Judges will need to add scores for this entry.{" "}
        <br></br><strong>Are you sure you want to add?</strong>
      </WarningBox>
    );
  };

  const renderEditContent = () => {
    const programChanged = oldProgramName !== newProgramName;
    const willReset = (divisionStatus && divisionStatus !== "judging") && (divisionChanged || programChanged);

    const statusBadge = (status?: string) => {
      if (!status) return null;
      const colors: Record<string, string> = { judging: '#e6a817', approved: '#3d9c3d', announced: '#1a7fc1', ready_to_announce: '#7b3db5' };
      return <span style={{ color: colors[status] ?? '#333', fontWeight: 600, textTransform: 'capitalize', fontSize: '12px' }}>{status.replace(/_/g, ' ')}</span>;
    };

    return (
      <Box>
        {divisionChanged && (
          <Box className="change-summary">
            <Typography variant="body2" fontWeight={600} mb={0.5}>Division Change:</Typography>
            <Typography variant="body2">From: <strong>{oldDivisionName}</strong> {oldDivisionStatus && <>({statusBadge(oldDivisionStatus)})</>}</Typography>
            <Typography variant="body2">To: <strong>{newDivisionName}</strong>{" "}{divisionStatus && (<>({statusBadge(divisionStatus)})</>)}</Typography>
            {programChanged && (
              <Box mt={1.5}>
                <Typography variant="body2" fontWeight={600} mb={0.5}>Program Change:</Typography>
                <Typography variant="body2">From: <strong>{oldProgramName}</strong></Typography>
                <Typography variant="body2">To: <strong>{newProgramName}</strong></Typography>
              </Box>
            )}
          </Box>
        )}
        {!divisionChanged && programChanged && (
          <Box className="change-summary">
            <Typography variant="body2" fontWeight={600} mb={0.5}>Program Change:</Typography>
            <Typography variant="body2">From: <strong>{oldProgramName}</strong></Typography>
            <Typography variant="body2">To: <strong>{newProgramName}</strong></Typography>
            {divisionStatus && (
              <Typography variant="body2" mt={0.5}><strong>{oldDivisionName} - {statusBadge(divisionStatus)}</strong></Typography>
            )}
          </Box>
        )}
        {willReset && (
          <WarningBox color="yellow">
            Making this change will <strong>re-announce the division</strong>. <strong>Are you sure you want to proceed?</strong>
          </WarningBox>
        )}
        {!divisionChanged && !programChanged && !willReset && (
          <Typography variant="body2">Are you sure you want to update entry <strong>#{entryNumber}</strong>?</Typography>
        )}
      </Box>
    );
  };

  const renderDeleteContent = () => {
    const scoresWillBeRemoved = divisionStatus && divisionStatus !== "judging";
    const hasTalktape = talktapeStatus === "ready";

    return (
      <Box>
        {scoresWillBeRemoved && (
          <Box mb={hasTalktape ? 2 : 0}>
            <WarningBox color="yellow">
              Division{" "}
              <a href={`/placements/${divisionId}`} target="_blank" rel="noopener noreferrer">
                <strong>{newDivisionName}</strong>
              </a>{" "}
              has <strong>approved/announced scores</strong>. Before deleting entry{" "}
              <strong>#{entryNumber}</strong>, first remove the scores using the steps below:
            </WarningBox>
            <Typography variant="body2" fontWeight={600} mt={1.5} mb={1}>Steps to remove scores:</Typography>
            <Typography variant="caption" display="block" color="text.secondary">Step 1: Click "Disapprove"</Typography>
            <Typography variant="caption" display="block" color="text.secondary" mb={0.5}>Step 2: Then delete the entry</Typography>
            <ImagePreview src="/images/scores_delete.png" alt="Delete scores steps" />
          </Box>
        )}
        {hasTalktape && (
          <>
            <WarningBox color="red">
              Entry <strong>#{entryNumber}</strong> has a <strong>Talktape video ready</strong>.{" "}
              Please <strong>remove the Talktape before deleting</strong> this entry.
            </WarningBox>
            <ImagePreview src="/images/talktape_delete_demo.png" alt="Remove talktape steps" />
          </>
        )}
        {!scoresWillBeRemoved && !hasTalktape && (
          <Typography variant="body2">
            Are you sure you want to delete entry <strong>#{entryNumber}</strong>? This action cannot be undone.
          </Typography>
        )}
      </Box>
    );
  };

  const contentMap = { add: renderAddContent, edit: renderEditContent, delete: renderDeleteContent };
  const blockConfirm = action === "delete" && (talktapeStatus === "ready" || divisionStatus !== "judging");

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box className="dialog-title">
          {ICONS[action]}
          <Typography variant="h6" fontWeight={600}>
            {TITLES[action]}{entryNumber ? ` #${entryNumber}` : ""}
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        {contentMap[action]()}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onCancel} variant="outlined" color="inherit">Cancel</Button>
          <Button onClick={onConfirm} variant="contained" color="primary" disabled={blockConfirm}>Yes, Proceed</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmActionDialog;